import React from 'react'
import { Link } from 'gatsby'
import { Icon } from '@chakra-ui/icons'
import { useLocation } from '@reach/router'
import { Box, Text, Center, Spacer } from '@chakra-ui/react'

import PinPageButton from './pin-page-button'
import { dragHandleSx, ephemeralPinSx, getExpandedOnlyDisplay } from './helpers'
import { SidebarItem } from '../tabs'
import { UserRoutes } from '~config/routes'
import SidebarItemIcon from '../sidebar-item/icon'
import { getIsActive } from '../sidebar-item/helpers'
import SharedStack from '~components/shared/wrappers/shared-stack'
import NewTag from '~components/shared/tags/new-tag'
import { FiExternalLink } from 'react-icons/fi'
import { FaGripVertical } from 'react-icons/fa6'

interface Props {
    page: SidebarItem
    sectionLabel: string
    /** Whether the sidebar itself is pinned open (controls label visibility). */
    isSidebarPinned: boolean
    showPin?: boolean
    /** Shows a drag-handle grip left of the icon (for draggable rows). */
    showDragHandle?: boolean
    /** Mirrors the legacy subpage styling when rendered inside a dropdown. */
    isSubPage?: boolean
    setSubmenu?: (value: React.SetStateAction<string>) => void
}

/**
 * Leaf page row for the reorganized sidebar. Mirrors the legacy sidebar item
 * visuals exactly, with a pin toggle for controlling sidebar visibility.
 */
const NewSidebarItem: React.FC<Props> = ({
    page,
    sectionLabel,
    isSidebarPinned,
    showPin = true,
    showDragHandle = false,
    isSubPage = false,
    setSubmenu,
}) => {
    const { pathname } = useLocation()
    const { to, label, icon, badge, isNew, isExternal, otherPaths } = page
    const isActive = getIsActive(to || '/', pathname, undefined, otherPaths)

    const content = (
        <SharedStack row align="center" spacing={1}>
            {showDragHandle && (
                <Box
                    className="sidebar-grip"
                    flexShrink={0}
                    alignItems="center"
                    {...getExpandedOnlyDisplay(isSidebarPinned)}
                >
                    <Icon as={FaGripVertical} boxSize="11px" />
                </Box>
            )}
            <SidebarItemIcon
                isActive={isActive}
                icon={icon}
                badgeCount={badge}
                showIndicator={!isSidebarPinned}
            />
            <SharedStack
                row
                align="center"
                spacing={1}
                minW={0}
                {...getExpandedOnlyDisplay(isSidebarPinned)}
                transition="opacity 0.2s ease-in-out"
            >
                <Text
                    noOfLines={1}
                    minW={0}
                    color={isActive ? 'white' : 'gray.300'}
                    fontWeight={isActive ? 'semibold' : 'normal'}
                    transition="0.2s"
                    fontSize="14px"
                >
                    {label}
                </Text>
                {isNew && <NewTag ml={1} colorScheme="brand" flexShrink={0} />}
                <Spacer />
                {!!badge && (
                    <Center
                        bg="brand.500"
                        rounded="full"
                        px={2}
                        mr={!isExternal && !showPin ? 2 : 0}
                        flexShrink={0}
                    >
                        <Text
                            fontSize="9px"
                            color="gray.900"
                            fontWeight="semibold"
                        >
                            {badge > 9 ? '9+' : badge}
                        </Text>
                    </Center>
                )}
                {isExternal && (
                    <Center w="32px" h="32px" flexShrink={0}>
                        <Icon
                            as={FiExternalLink}
                            boxSize="13px"
                            color="gray.400"
                        />
                    </Center>
                )}
                {showPin && to && (
                    <Box className="sidebar-pin" flexShrink={0}>
                        <PinPageButton
                            to={to}
                            label={label}
                            sectionLabel={sectionLabel}
                        />
                    </Box>
                )}
            </SharedStack>
        </SharedStack>
    )

    return (
        <Box
            w="full"
            sx={{ ...ephemeralPinSx, ...dragHandleSx }}
            borderWidth={isSubPage ? 0 : 1}
            borderColor={isActive ? 'gray.700' : 'transparent'}
            _hover={{
                borderColor: 'gray.700',
                bg: isSubPage ? 'gray.700' : 'gray.800',
            }}
            transition="all 0.2s"
            bg={
                isActive ? (isSubPage ? 'gray.700' : 'gray.800') : 'transparent'
            }
            rounded="lg"
            pl={isSidebarPinned && isSubPage ? 2 : 0}
            _groupHover={{
                pl: isSubPage ? 2 : 0,
            }}
        >
            {isExternal ? (
                <Box as="a" href={to} target="_blank">
                    {content}
                </Box>
            ) : (
                <Box
                    cursor="pointer"
                    onClick={() => !isSubPage && setSubmenu?.('none')}
                >
                    <Link to={to || UserRoutes.Home}>{content}</Link>
                </Box>
            )}
        </Box>
    )
}

export default NewSidebarItem
